import { useState, useEffect } from 'react';
import { Power, Save, RotateCcw, RefreshCw, Zap, Trash2 } from 'lucide-react';
import { appToast as toast } from '@/lib/app-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { RoleGate } from '@/components/common/RoleGate';
import { useIsAdmin } from '@/auth/AuthProvider';
import {
  useAdminActivateVersion,
  useAdminRunCustomerSync,
  useAdminRunDeliveryTermsSync,
  useAdminRunDeliveryModeSync,
  useAdminRunExactProductSync,
  useAdminRunLegalEntitySync,
  useAdminRunPaymentTermsSync,
  useAdminRunProductSync,
  useAdminRunSiteSync,
  useAdminRunUnitOfMeasureSync,
  useAdminRunEmbeddingSync,
  useAdminResetSync,
  useAdminState,
  useAdminSaveMailConfig,
  useAdminResetMail,
  useAdminTriggerStagingBatch,
  useAdminUpdateTunables,
} from '@/features/admin/hooks';
import { cn, formatRelativeTime } from '@/lib/utils';
import type { AdminTunables, ConfigVersion, QueueGroupingField } from '@/api/cockpit';

function parseNum(v: string, fallback: number): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

const QUEUE_GROUPING_OPTIONS: readonly { value: QueueGroupingField; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'customer', label: 'Customer' },
  { value: 'po', label: 'PO' },
  { value: 'salesOrder', label: 'Sales order' },
];

export function AdminPage(): JSX.Element {
  return (
    <RoleGate allow={['SO-Supervisor', 'SO-Admin']}>
      <AdminInner />
    </RoleGate>
  );
}

function AdminInner(): JSX.Element {
  const canMutate = useIsAdmin();
  const { data, isLoading, isError, error, refetch, isFetching } = useAdminState();
  const saveMailConfig = useAdminSaveMailConfig();
  const resetMail = useAdminResetMail();
  const updateTunables = useAdminUpdateTunables();
  const activate = useAdminActivateVersion();
  const runCustomerSync = useAdminRunCustomerSync();
  const runProductSync = useAdminRunProductSync();
  const runExactProductSync = useAdminRunExactProductSync();
  const runLegalEntitySync = useAdminRunLegalEntitySync();
  const runSiteSync = useAdminRunSiteSync();
  const runPaymentTermsSync = useAdminRunPaymentTermsSync();
  const runDeliveryTermsSync = useAdminRunDeliveryTermsSync();
  const runDeliveryModeSync = useAdminRunDeliveryModeSync();
  const runUnitOfMeasureSync = useAdminRunUnitOfMeasureSync();
  const runEmbeddingSync = useAdminRunEmbeddingSync();
  const triggerStagingBatch = useAdminTriggerStagingBatch();
  const [stagingBatchDataAreaId, setStagingBatchDataAreaId] = useState<string | null>(null);
  const [exactProductCode, setExactProductCode] = useState('');

  const [tunables, setTunables] = useState<AdminTunables | null>(null);
  useEffect(() => {
    if (data) setTunables(data.tunables);
  }, [data]);

  const [mailForm, setMailForm] = useState({ tenantId: '', clientId: '', clientSecret: '' });
  useEffect(() => {
    if (data?.mailAccount) {
      setMailForm((prev) => ({
        tenantId: data.mailAccount.tenantId ?? prev.tenantId,
        clientId: data.mailAccount.clientId ?? prev.clientId,
        // The client secret is write-only; never hydrate it from the server.
        clientSecret: '',
      }));
    }
  }, [data]);

  // Surface the outcome of the OAuth round trip (the backend redirects back
  // with a query flag) and strip the flag from the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('mailConnected')) {
      toast.success('Mailbox connected.');
    }
    const mailError = params.get('mailError');
    if (mailError) {
      toast.error(`Mailbox login failed: ${mailError}`);
    }
    if (params.has('mailConnected') || params.has('mailError')) {
      params.delete('mailConnected');
      params.delete('mailError');
      const query = params.toString();
      window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
    }
  }, []);

  const handleSaveMail = (): void => {
    if (!mailForm.tenantId.trim() || !mailForm.clientId.trim() || !mailForm.clientSecret.trim()) {
      toast.error('Tenant ID, Client ID, and Client secret are all required.');
      return;
    }
    saveMailConfig.mutate(mailForm, {
      onSuccess: () => toast.success('Mail configuration saved.'),
      onError: () => toast.error('Failed to save mail configuration.'),
    });
  };

  if (isLoading || !data || !tunables) {
    if (!isLoading && isError) {
      return (
        <Card>
          <CardContent className="flex items-center justify-between gap-4 py-8">
            <div className="text-sm text-muted-foreground">
              Failed to load admin state. {error instanceof Error ? error.message : 'Unknown error.'}
            </div>
            <Button variant="outline" size="sm" onClick={() => void refetch()} disabled={isFetching}>
              Retry
            </Button>
          </CardContent>
        </Card>
      );
    }
    return <Skeleton className="h-96 w-full" />;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Admin</h1>
        <p className="text-sm text-muted-foreground">
          Mailbox service, model tunables, and config versions.
        </p>
        {!canMutate && (
          <div className="mt-2 inline-flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-xs text-amber-700 dark:text-amber-400">
            Read-only access (SO-Supervisor). Mutations require SO-Admin.
          </div>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Mail service</CardTitle>
            <CardDescription>
              Delegated Microsoft Graph mailbox reader. Configure the Entra app for the mailbox
              account (it may live in a different tenant), then sign in to grant access. The
              backend worker polls the inbox, feeds each email into the pipeline, and marks it read.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2">
              <Badge variant={data.mailAccount.connected ? 'success' : 'destructive'}>
                {data.mailAccount.connected
                  ? 'Connected'
                  : data.mailAccount.configured
                    ? 'Configured'
                    : 'Not configured'}
              </Badge>
              {data.mailAccount.accountUsername && (
                <span className="text-xs text-muted-foreground">
                  {data.mailAccount.accountUsername}
                </span>
              )}
              {data.mailAccount.lastPollAt && (
                <span className="text-xs text-muted-foreground">
                  last poll {formatRelativeTime(data.mailAccount.lastPollAt)}
                </span>
              )}
            </div>
            {data.mailAccount.lastPollError && (
              <p className="text-xs text-destructive">
                Last poll error: {data.mailAccount.lastPollError}
              </p>
            )}
            <div className="space-y-2">
              <div className="space-y-1">
                <Label htmlFor="mail-tenant">Tenant ID</Label>
                <Input
                  id="mail-tenant"
                  value={mailForm.tenantId}
                  onChange={(e) => setMailForm({ ...mailForm, tenantId: e.target.value })}
                  disabled={!canMutate}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="mail-client">Client ID</Label>
                <Input
                  id="mail-client"
                  value={mailForm.clientId}
                  onChange={(e) => setMailForm({ ...mailForm, clientId: e.target.value })}
                  disabled={!canMutate}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="mail-secret">
                  Client secret
                  {data.mailAccount.configured && (
                    <span className="ml-1 text-xs text-muted-foreground">
                      (configured — re-enter to change)
                    </span>
                  )}
                </Label>
                <Input
                  id="mail-secret"
                  type="password"
                  value={mailForm.clientSecret}
                  onChange={(e) => setMailForm({ ...mailForm, clientSecret: e.target.value })}
                  placeholder={data.mailAccount.configured ? '••••••••' : ''}
                  disabled={!canMutate}
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={handleSaveMail} disabled={!canMutate || saveMailConfig.isPending}>
                <Save className="h-4 w-4" />
                Save
              </Button>
              <Button
                variant="default"
                onClick={() => window.location.assign('/api/v1/cockpit/admin/mail/login')}
                disabled={!data.mailAccount.configured}
              >
                <Power className="h-4 w-4" />
                Login
              </Button>
              <Button
                variant="destructive"
                onClick={() => resetMail.mutate()}
                disabled={
                  !canMutate ||
                  resetMail.isPending ||
                  (!data.mailAccount.configured && !data.mailAccount.connected)
                }
              >
                <Trash2 className="h-4 w-4" />
                Reset
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>D365 staging batch job</CardTitle>
            <CardDescription>
              Fires the <code className="font-mono text-xs">startBatchJob</code> OData action
              bound to <code className="font-mono text-xs">tpmDI_DirectSalesOrders</code> for
              the selected legal entity. Use this when a staging row is sitting in{' '}
              <em>Validating in D365</em> or <em>Creating Sales Order</em> and you do not
              want to wait for the next scheduled sweep.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <SelectTunableRow
              label="Legal entity (_company)"
              value={stagingBatchDataAreaId}
              placeholder="Pick a legal entity"
              options={data.legalEntitySync.samples.map((sample) => ({
                value: sample.legalEntityId,
                label: `${sample.legalEntityId.toUpperCase()} - ${sample.legalEntityName}`,
              }))}
              onChange={(value) => setStagingBatchDataAreaId(value)}
              disabled={!canMutate}
            />
            <div className="flex items-center justify-end gap-2">
              <Button
                size="sm"
                disabled={
                  !canMutate ||
                  !stagingBatchDataAreaId ||
                  triggerStagingBatch.isPending
                }
                onClick={() => {
                  if (!stagingBatchDataAreaId) return;
                  triggerStagingBatch.mutate(stagingBatchDataAreaId, {
                    onSuccess: (res) => {
                      toast.success(
                        `Batch job triggered for ${res.dataAreaId.toUpperCase()}.`,
                      );
                    },
                    onError: (err) => {
                      toast.error(
                        err instanceof Error
                          ? `Trigger failed: ${err.message}`
                          : 'Trigger failed.',
                      );
                    },
                  });
                }}
              >
                <Zap className="h-4 w-4" />
                Trigger batch now
              </Button>
            </div>
            {data.legalEntitySync.samples.length === 0 && (
              <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
                No legal entities in the local index yet. Run the legal entity sync card
                below first.
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Tunables</CardTitle>
            <CardDescription>
              Classifier and match thresholds. Changes create a new config version.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <TunableRow
              label="Classifier threshold"
              value={tunables.classifierThreshold}
              onChange={(v) => setTunables({ ...tunables, classifierThreshold: v })}
              disabled={!canMutate}
            />
            <TunableRow
              label="Customer match threshold"
              value={tunables.customerMatchThreshold}
              onChange={(v) => setTunables({ ...tunables, customerMatchThreshold: v })}
              disabled={!canMutate}
            />
            <TunableRow
              label="Product match threshold"
              value={tunables.productMatchThreshold}
              onChange={(v) => setTunables({ ...tunables, productMatchThreshold: v })}
              disabled={!canMutate}
            />
            <SelectTunableRow
              label="Default legal entity"
              value={tunables.defaultLegalEntityId}
              placeholder="No fallback legal entity"
              options={data.legalEntitySync.samples.map((sample) => ({
                value: sample.legalEntityId,
                label: `${sample.legalEntityId.toUpperCase()} - ${sample.legalEntityName}`,
              }))}
              onChange={(value) => setTunables({ ...tunables, defaultLegalEntityId: value })}
              disabled={!canMutate}
            />
            <SelectTunableRow
              label="Default site"
              value={tunables.defaultSiteId}
              placeholder="No fallback site"
              options={data.siteSync.samples.map((sample) => ({
                value: sample.siteId,
                label: `${sample.siteId} - ${sample.siteName}`,
              }))}
              onChange={(value) => setTunables({ ...tunables, defaultSiteId: value })}
              disabled={!canMutate}
            />
            <BooleanTunableRow
              label="Accept duplicated documents"
              description="Do not close duplicates. Forward them through processing and surface an informative warning instead."
              value={tunables.acceptDuplicatedDocuments}
              onChange={(value) =>
                setTunables({ ...tunables, acceptDuplicatedDocuments: value })
              }
              disabled={!canMutate}
            />
            <BooleanTunableRow
              label="Site feedback fallback"
              description="When the site-matching rule fails, use a confident operator feedback as the resolved site instead of parking the row on the duplicate-review exception."
              value={tunables.siteFeedbackFallbackEnabled}
              onChange={(value) =>
                setTunables({ ...tunables, siteFeedbackFallbackEnabled: value })
              }
              disabled={!canMutate}
            />
            <BooleanTunableRow
              label="Queue grouping enabled"
              description="When enabled, Customer Care sees Admin-selected group headers. When disabled, the queue keeps the flat row list and only shows the Received on column change."
              value={tunables.queueGroupingEnabled}
              onChange={(value) =>
                setTunables({ ...tunables, queueGroupingEnabled: value })
              }
              disabled={!canMutate}
            />
            <SelectTunableRow
              label="Queue grouping"
              value={tunables.queueGroupingField}
              placeholder="PO"
              options={QUEUE_GROUPING_OPTIONS}
              onChange={(value) =>
                setTunables({
                  ...tunables,
                  queueGroupingField: (value ?? 'po') as QueueGroupingField,
                })
              }
              disabled={!canMutate}
            />
            <BooleanTunableRow
              label="Queue groups default expanded"
              description="Controls whether queue groups are expanded or collapsed when Customer Care opens the queue."
              value={tunables.queueGroupsDefaultExpanded}
              onChange={(value) =>
                setTunables({ ...tunables, queueGroupsDefaultExpanded: value })
              }
              disabled={!canMutate}
            />
            <Separator />
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTunables(data.tunables)}
                disabled={!canMutate || updateTunables.isPending}
              >
                Reset
              </Button>
              <Button
                size="sm"
                onClick={() => updateTunables.mutate(tunables)}
                disabled={!canMutate || updateTunables.isPending}
              >
                <Save className="h-4 w-4" />
                Save tunables
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Customer Sync</CardTitle>
            <CardDescription>
              Trigger an on-demand D365 customer sync into the local customer index.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="text-sm text-muted-foreground">
                Uses the same customer sync job as the daily scheduler and emits live SSE notifications.
              </div>
              <div className="flex items-center gap-2">
                <ResetSyncButton
                  target="customers"
                  label="Reset"
                  confirmMessage="Reset the customer sync? This clears the local customer index and watermark so the next run is a full refresh."
                  canMutate={canMutate}
                />
                <Button
                  onClick={() =>
                    runCustomerSync.mutate(undefined, {
                      onError: (err) => toast.error(apiErrorMessage(err, 'Customer sync failed.')),
                    })
                  }
                  disabled={!canMutate || runCustomerSync.isPending}
                >
                  <RefreshCw className="h-4 w-4" />
                  Run now
                </Button>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <StatTile
                label="Last sync"
                value={data.customerSync.lastSyncAt ? formatRelativeTime(data.customerSync.lastSyncAt) : 'Never'}
                helper={
                  data.customerSync.lastSyncAt
                    ? new Date(data.customerSync.lastSyncAt).toLocaleString()
                    : 'No completed customer sync yet'
                }
              />
              <StatTile
                label="Customers indexed"
                value={String(data.customerSync.indexedCustomerCount)}
                helper={
                  data.customerSync.latestIndexedAt
                    ? `Latest indexed row ${formatRelativeTime(data.customerSync.latestIndexedAt)}`
                    : 'No customer rows in the local index yet'
                }
              />
              <StatTile
                label="Duration"
                value={formatDurationMs(data.customerSync.lastSyncDurationMs)}
                helper={
                  data.customerSync.lastSyncError
                    ? `Last error: ${data.customerSync.lastSyncError}`
                    : 'No sync error recorded'
                }
              />
            </div>

            <div className="space-y-2">
              <div>
                <div className="text-sm font-medium">Latest synced customers</div>
                <div className="text-xs text-muted-foreground">
                  Most recent five customer index rows by sync timestamp.
                </div>
              </div>
              {data.customerSync.samples.length === 0 ? (
                <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                  No customer sync samples are available yet.
                </div>
              ) : (
                <div className="space-y-2">
                  {data.customerSync.samples.map((sample) => (
                    <div
                      key={sample.id}
                      className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                    >
                      <div>
                        <div className="font-medium">{sample.customerName}</div>
                        <div className="text-xs text-muted-foreground">{sample.customerNumber}</div>
                      </div>
                      <div className="text-right text-xs text-muted-foreground">
                        <div>{formatRelativeTime(sample.syncedAt)}</div>
                        <div>{new Date(sample.syncedAt).toLocaleString()}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Product Sync</CardTitle>
            <CardDescription>
              Trigger an on-demand D365 released-product sync into the local product index.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="text-sm text-muted-foreground">
                Uses the same product sync job as the daily scheduler and emits live SSE notifications.
              </div>
              <div className="flex items-center gap-2">
                <ResetSyncButton
                  target="products"
                  label="Reset"
                  confirmMessage="Reset the product sync? This clears the local product index and watermark so the next run is a full refresh."
                  canMutate={canMutate}
                />
                <Button
                  onClick={() =>
                    runProductSync.mutate(undefined, {
                      onError: (err) => toast.error(apiErrorMessage(err, 'Product sync failed.')),
                    })
                  }
                  disabled={!canMutate || runProductSync.isPending}
                >
                  <RefreshCw className="h-4 w-4" />
                  Run now
                </Button>
              </div>
            </div>

            <form
              className="grid gap-2 rounded-md border bg-muted/20 p-3 sm:grid-cols-[1fr_auto]"
              onSubmit={(event) => {
                event.preventDefault();
                const itemNumber = exactProductCode.trim();
                if (!itemNumber) {
                  toast.error('Enter an exact product code.');
                  return;
                }
                runExactProductSync.mutate(itemNumber, {
                  onSuccess: (result) => {
                    toast.success(
                      result.recordCount > 0
                        ? `Synced ${result.recordCount} product row${result.recordCount === 1 ? '' : 's'} for ${itemNumber}.`
                        : `No D365 product rows found for ${itemNumber}.`,
                    );
                  },
                  onError: (err) =>
                    toast.error(
                      err instanceof Error ? `Product sync failed: ${err.message}` : 'Product sync failed.',
                    ),
                });
              }}
            >
              <div className="space-y-1">
                <Label htmlFor="exact-product-sync">Exact product code</Label>
                <Input
                  id="exact-product-sync"
                  value={exactProductCode}
                  onChange={(event) => setExactProductCode(event.target.value)}
                  placeholder="e.g. COL_PM412071"
                  disabled={!canMutate || runExactProductSync.isPending}
                />
                <div className="text-xs text-muted-foreground">
                  Fetches this product from every D365 legal entity, embeds it immediately, and saves it to the local index.
                </div>
              </div>
              <div className="flex items-end">
                <Button
                  type="submit"
                  disabled={!canMutate || runExactProductSync.isPending || exactProductCode.trim().length === 0}
                >
                  <RefreshCw className={cn('h-4 w-4', runExactProductSync.isPending && 'animate-spin')} />
                  Sync
                </Button>
              </div>
            </form>

            <div className="grid gap-3 sm:grid-cols-3">
              <StatTile
                label="Last sync"
                value={data.productSync.lastSyncAt ? formatRelativeTime(data.productSync.lastSyncAt) : 'Never'}
                helper={
                  data.productSync.lastSyncAt
                    ? new Date(data.productSync.lastSyncAt).toLocaleString()
                    : 'No completed product sync yet'
                }
              />
              <StatTile
                label="Products indexed"
                value={String(data.productSync.indexedProductCount)}
                helper={
                  data.productSync.latestIndexedAt
                    ? `Latest indexed row ${formatRelativeTime(data.productSync.latestIndexedAt)}`
                    : 'No product rows in the local index yet'
                }
              />
              <StatTile
                label="Duration"
                value={formatDurationMs(data.productSync.lastSyncDurationMs)}
                helper={
                  data.productSync.lastSyncError
                    ? `Last error: ${data.productSync.lastSyncError}`
                    : 'No sync error recorded'
                }
              />
            </div>

            <div className="space-y-2">
              <div>
                <div className="text-sm font-medium">Latest synced products</div>
                <div className="text-xs text-muted-foreground">
                  Most recent five product index rows by sync timestamp.
                </div>
              </div>
              {data.productSync.samples.length === 0 ? (
                <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                  No product sync samples are available yet.
                </div>
              ) : (
                <div className="space-y-2">
                  {data.productSync.samples.map((sample) => (
                    <div
                      key={sample.id}
                      className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                    >
                      <div>
                        <div className="font-medium">{sample.productName}</div>
                        <div className="text-xs text-muted-foreground">{sample.itemNumber}</div>
                      </div>
                      <div className="text-right text-xs text-muted-foreground">
                        <div>{formatRelativeTime(sample.syncedAt)}</div>
                        <div>{new Date(sample.syncedAt).toLocaleString()}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Legal Entity Sync</CardTitle>
            <CardDescription>
              Trigger an on-demand D365 legal-entity sync for company/address/contact matching context.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="text-sm text-muted-foreground">
                Pulls real D365 legal entities, including address, phone, and email fields used by matching review.
              </div>
              <div className="flex items-center gap-2">
                <ResetSyncButton
                  target="legal-entities"
                  label="Reset"
                  confirmMessage="Reset the legal-entity sync? This drops the local snapshot and watermark so the next run is a full refresh."
                  canMutate={canMutate}
                />
                <Button onClick={() => runLegalEntitySync.mutate()} disabled={!canMutate || runLegalEntitySync.isPending}>
                  <RefreshCw className="h-4 w-4" />
                  Run now
                </Button>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <StatTile
                label="Last sync"
                value={data.legalEntitySync.lastSyncAt ? formatRelativeTime(data.legalEntitySync.lastSyncAt) : 'Never'}
                helper={
                  data.legalEntitySync.lastSyncAt
                    ? new Date(data.legalEntitySync.lastSyncAt).toLocaleString()
                    : 'No completed legal-entity sync yet'
                }
              />
              <StatTile
                label="Legal entities indexed"
                value={String(data.legalEntitySync.indexedLegalEntityCount)}
                helper={
                  data.legalEntitySync.latestIndexedAt
                    ? `Latest indexed row ${formatRelativeTime(data.legalEntitySync.latestIndexedAt)}`
                    : 'No legal entity snapshot rows yet'
                }
              />
              <StatTile
                label="Duration"
                value={formatDurationMs(data.legalEntitySync.lastSyncDurationMs)}
                helper={
                  data.legalEntitySync.lastSyncError
                    ? `Last error: ${data.legalEntitySync.lastSyncError}`
                    : 'No sync error recorded'
                }
              />
            </div>

            <div className="space-y-2">
              <div>
                <div className="text-sm font-medium">Latest synced legal entities</div>
                <div className="text-xs text-muted-foreground">
                  Most recent five legal entities from the latest snapshot.
                </div>
              </div>
              {data.legalEntitySync.samples.length === 0 ? (
                <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                  No legal entity sync samples are available yet.
                </div>
              ) : (
                <div className="space-y-2">
                  {data.legalEntitySync.samples.map((sample) => (
                    <div
                      key={sample.id}
                      className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                    >
                      <div>
                        <div className="font-medium">{sample.legalEntityName}</div>
                        <div className="text-xs text-muted-foreground">{sample.legalEntityId}</div>
                      </div>
                      <div className="text-right text-xs text-muted-foreground">
                        <div>{formatRelativeTime(sample.syncedAt)}</div>
                        <div>{new Date(sample.syncedAt).toLocaleString()}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Site Sync</CardTitle>
            <CardDescription>
              Trigger an on-demand D365 site sync for branch/site address matching context.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="text-sm text-muted-foreground">
                Pulls real operational sites and their current postal addresses from D365.
              </div>
              <div className="flex items-center gap-2">
                <ResetSyncButton
                  target="sites"
                  label="Reset"
                  confirmMessage="Reset the site sync? This drops the local snapshot and watermark so the next run is a full refresh."
                  canMutate={canMutate}
                />
                <Button onClick={() => runSiteSync.mutate()} disabled={!canMutate || runSiteSync.isPending}>
                  <RefreshCw className="h-4 w-4" />
                  Run now
                </Button>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <StatTile
                label="Last sync"
                value={data.siteSync.lastSyncAt ? formatRelativeTime(data.siteSync.lastSyncAt) : 'Never'}
                helper={
                  data.siteSync.lastSyncAt
                    ? new Date(data.siteSync.lastSyncAt).toLocaleString()
                    : 'No completed site sync yet'
                }
              />
              <StatTile
                label="Sites indexed"
                value={String(data.siteSync.indexedSiteCount)}
                helper={
                  data.siteSync.latestIndexedAt
                    ? `Latest indexed row ${formatRelativeTime(data.siteSync.latestIndexedAt)}`
                    : 'No site snapshot rows yet'
                }
              />
              <StatTile
                label="Duration"
                value={formatDurationMs(data.siteSync.lastSyncDurationMs)}
                helper={
                  data.siteSync.lastSyncError
                    ? `Last error: ${data.siteSync.lastSyncError}`
                    : 'No sync error recorded'
                }
              />
            </div>

            <div className="space-y-2">
              <div>
                <div className="text-sm font-medium">Latest synced sites</div>
                <div className="text-xs text-muted-foreground">
                  Most recent five sites from the latest snapshot.
                </div>
              </div>
              {data.siteSync.samples.length === 0 ? (
                <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                  No site sync samples are available yet.
                </div>
              ) : (
                <div className="space-y-2">
                  {data.siteSync.samples.map((sample) => (
                    <div
                      key={sample.id}
                      className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                    >
                      <div>
                        <div className="font-medium">{sample.siteName}</div>
                        <div className="text-xs text-muted-foreground">{sample.siteId}</div>
                      </div>
                      <div className="text-right text-xs text-muted-foreground">
                        <div>{formatRelativeTime(sample.syncedAt)}</div>
                        <div>{new Date(sample.syncedAt).toLocaleString()}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Payment Terms Sync</CardTitle>
            <CardDescription>
              Trigger an on-demand D365 payment terms candidate sync.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="text-sm text-muted-foreground">
                Pulls the payment terms catalog used by header field matching.
              </div>
              <div className="flex items-center gap-2">
                <ResetSyncButton
                  target="payment-terms"
                  label="Reset"
                  confirmMessage="Reset the payment-terms sync? This drops the local snapshot and watermark so the next run is a full refresh."
                  canMutate={canMutate}
                />
                <Button onClick={() => runPaymentTermsSync.mutate()} disabled={!canMutate || runPaymentTermsSync.isPending}>
                  <RefreshCw className="h-4 w-4" />
                  Run now
                </Button>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <StatTile
                label="Last sync"
                value={data.paymentTermsSync.lastSyncAt ? formatRelativeTime(data.paymentTermsSync.lastSyncAt) : 'Never'}
                helper={
                  data.paymentTermsSync.lastSyncAt
                    ? new Date(data.paymentTermsSync.lastSyncAt).toLocaleString()
                    : 'No completed payment terms sync yet'
                }
              />
              <StatTile
                label="Terms indexed"
                value={String(data.paymentTermsSync.indexedPaymentTermsCount)}
                helper={
                  data.paymentTermsSync.latestIndexedAt
                    ? `Latest indexed row ${formatRelativeTime(data.paymentTermsSync.latestIndexedAt)}`
                    : 'No payment terms snapshot yet'
                }
              />
              <StatTile
                label="Duration"
                value={formatDurationMs(data.paymentTermsSync.lastSyncDurationMs)}
                helper={
                  data.paymentTermsSync.lastSyncError
                    ? `Last error: ${data.paymentTermsSync.lastSyncError}`
                    : 'No sync error recorded'
                }
              />
            </div>

            <SimpleSyncSampleList
              title="Latest payment terms"
              description="Most recent five payment terms from the latest snapshot."
              emptyMessage="No payment terms sync samples are available yet."
              items={data.paymentTermsSync.samples.map((sample) => ({
                id: sample.id,
                title: sample.code,
                subtitle: sample.label,
                syncedAt: sample.syncedAt,
              }))}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Delivery Terms Sync</CardTitle>
            <CardDescription>
              Trigger an on-demand D365 delivery terms candidate sync.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="text-sm text-muted-foreground">
                Pulls the delivery terms catalog used for FOB and incoterms matching.
              </div>
              <div className="flex items-center gap-2">
                <ResetSyncButton
                  target="delivery-terms"
                  label="Reset"
                  confirmMessage="Reset the delivery-terms sync? This drops the local snapshot and watermark so the next run is a full refresh."
                  canMutate={canMutate}
                />
                <Button onClick={() => runDeliveryTermsSync.mutate()} disabled={!canMutate || runDeliveryTermsSync.isPending}>
                  <RefreshCw className="h-4 w-4" />
                  Run now
                </Button>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <StatTile
                label="Last sync"
                value={data.deliveryTermsSync.lastSyncAt ? formatRelativeTime(data.deliveryTermsSync.lastSyncAt) : 'Never'}
                helper={
                  data.deliveryTermsSync.lastSyncAt
                    ? new Date(data.deliveryTermsSync.lastSyncAt).toLocaleString()
                    : 'No completed delivery terms sync yet'
                }
              />
              <StatTile
                label="Terms indexed"
                value={String(data.deliveryTermsSync.indexedDeliveryTermsCount)}
                helper={
                  data.deliveryTermsSync.latestIndexedAt
                    ? `Latest indexed row ${formatRelativeTime(data.deliveryTermsSync.latestIndexedAt)}`
                    : 'No delivery terms snapshot yet'
                }
              />
              <StatTile
                label="Duration"
                value={formatDurationMs(data.deliveryTermsSync.lastSyncDurationMs)}
                helper={
                  data.deliveryTermsSync.lastSyncError
                    ? `Last error: ${data.deliveryTermsSync.lastSyncError}`
                    : 'No sync error recorded'
                }
              />
            </div>

            <SimpleSyncSampleList
              title="Latest delivery terms"
              description="Most recent five delivery terms from the latest snapshot."
              emptyMessage="No delivery terms sync samples are available yet."
              items={data.deliveryTermsSync.samples.map((sample) => ({
                id: sample.id,
                title: sample.code,
                subtitle: sample.label,
                syncedAt: sample.syncedAt,
              }))}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Delivery Mode Sync</CardTitle>
            <CardDescription>
              Trigger an on-demand D365 delivery mode candidate sync.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="text-sm text-muted-foreground">
                Pulls the delivery mode catalog used by header field matching.
              </div>
              <div className="flex items-center gap-2">
                <ResetSyncButton
                  target="delivery-modes"
                  label="Reset"
                  confirmMessage="Reset the delivery-mode sync? This drops the local snapshot and watermark so the next run is a full refresh."
                  canMutate={canMutate}
                />
                <Button onClick={() => runDeliveryModeSync.mutate()} disabled={!canMutate || runDeliveryModeSync.isPending}>
                  <RefreshCw className="h-4 w-4" />
                  Run now
                </Button>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <StatTile
                label="Last sync"
                value={data.deliveryModeSync.lastSyncAt ? formatRelativeTime(data.deliveryModeSync.lastSyncAt) : 'Never'}
                helper={
                  data.deliveryModeSync.lastSyncAt
                    ? new Date(data.deliveryModeSync.lastSyncAt).toLocaleString()
                    : 'No completed delivery mode sync yet'
                }
              />
              <StatTile
                label="Modes indexed"
                value={String(data.deliveryModeSync.indexedDeliveryModeCount)}
                helper={
                  data.deliveryModeSync.latestIndexedAt
                    ? `Latest indexed row ${formatRelativeTime(data.deliveryModeSync.latestIndexedAt)}`
                    : 'No delivery mode snapshot yet'
                }
              />
              <StatTile
                label="Duration"
                value={formatDurationMs(data.deliveryModeSync.lastSyncDurationMs)}
                helper={
                  data.deliveryModeSync.lastSyncError
                    ? `Last error: ${data.deliveryModeSync.lastSyncError}`
                    : 'No sync error recorded'
                }
              />
            </div>

            <SimpleSyncSampleList
              title="Latest delivery modes"
              description="Most recent five delivery modes from the latest snapshot."
              emptyMessage="No delivery mode sync samples are available yet."
              items={data.deliveryModeSync.samples.map((sample) => ({
                id: sample.id,
                title: sample.code,
                subtitle: sample.label,
                syncedAt: sample.syncedAt,
              }))}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Unit Of Measure Sync</CardTitle>
            <CardDescription>
              Trigger an on-demand D365 units-of-measure sync into the local unit index.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="text-sm text-muted-foreground">
                Pulls the unit catalog and embeddings used by UOM line matching.
              </div>
              <div className="flex items-center gap-2">
                <ResetSyncButton
                  target="units-of-measure"
                  label="Reset"
                  confirmMessage="Reset the units-of-measure sync? This clears the local unit index and watermark so the next run is a full refresh."
                  canMutate={canMutate}
                />
                <Button onClick={() => runUnitOfMeasureSync.mutate()} disabled={!canMutate || runUnitOfMeasureSync.isPending}>
                  <RefreshCw className="h-4 w-4" />
                  Run now
                </Button>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <StatTile
                label="Last sync"
                value={data.unitOfMeasureSync.lastSyncAt ? formatRelativeTime(data.unitOfMeasureSync.lastSyncAt) : 'Never'}
                helper={
                  data.unitOfMeasureSync.lastSyncAt
                    ? new Date(data.unitOfMeasureSync.lastSyncAt).toLocaleString()
                    : 'No completed unit sync yet'
                }
              />
              <StatTile
                label="Units indexed"
                value={String(data.unitOfMeasureSync.indexedUnitOfMeasureCount)}
                helper={
                  data.unitOfMeasureSync.latestIndexedAt
                    ? `Latest indexed row ${formatRelativeTime(data.unitOfMeasureSync.latestIndexedAt)}`
                    : 'No unit rows in the local index yet'
                }
              />
              <StatTile
                label="Duration"
                value={formatDurationMs(data.unitOfMeasureSync.lastSyncDurationMs)}
                helper={
                  data.unitOfMeasureSync.lastSyncError
                    ? `Last error: ${data.unitOfMeasureSync.lastSyncError}`
                    : 'No sync error recorded'
                }
              />
            </div>

            <SimpleSyncSampleList
              title="Latest units of measure"
              description="Most recent five unit rows by sync timestamp."
              emptyMessage="No unit sync samples are available yet."
              items={data.unitOfMeasureSync.samples.map((sample) => ({
                id: sample.id,
                title: sample.symbol,
                subtitle: sample.description,
                syncedAt: sample.syncedAt,
              }))}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Embedding Backfill</CardTitle>
            <CardDescription>
              Fills the vector columns the D365 syncs leave empty. Runs automatically
              on its own cadence (the embeddings endpoint has tighter rate limits than
              D365), so this is mostly here for visibility plus a way to force an
              immediate pass after a large sync.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                {data.embeddingSync.pendingTotal > 0 ? (
                  <Badge variant="warning">
                    {data.embeddingSync.pendingTotal} rows awaiting embedding
                  </Badge>
                ) : (
                  <Badge variant="success">Index fully embedded</Badge>
                )}
                {data.embeddingSync.pendingTotal > 0 && (
                  <span className="text-xs text-muted-foreground">
                    Vector matching can&apos;t see these rows until they are embedded.
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <ResetSyncButton
                  target="embeddings"
                  label="Re-embed all"
                  confirmMessage="Clear every stored vector and re-embed the whole index? The backfill will then regenerate all embeddings from scratch."
                  canMutate={canMutate}
                />
                <Button
                  onClick={() => runEmbeddingSync.mutate()}
                  disabled={!canMutate || runEmbeddingSync.isPending}
                >
                  <RefreshCw className="h-4 w-4" />
                  Backfill now
                </Button>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <StatTile
                label="Pending (customers)"
                value={String(data.embeddingSync.pendingCustomerCount)}
                helper="Customer rows without a current vector"
              />
              <StatTile
                label="Pending (products)"
                value={String(data.embeddingSync.pendingProductCount)}
                helper="Product rows without a current vector"
              />
              <StatTile
                label="Pending (units)"
                value={String(data.embeddingSync.pendingUnitOfMeasureCount)}
                helper="Unit-of-measure rows without a current vector"
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <StatTile
                label="Last backfill"
                value={
                  data.embeddingSync.lastSyncAt
                    ? formatRelativeTime(data.embeddingSync.lastSyncAt)
                    : 'Never'
                }
                helper={
                  data.embeddingSync.lastSyncAt
                    ? new Date(data.embeddingSync.lastSyncAt).toLocaleString()
                    : 'No completed backfill yet'
                }
              />
              <StatTile
                label="Embedded last run"
                value={
                  data.embeddingSync.lastSyncRecordCount !== null
                    ? String(data.embeddingSync.lastSyncRecordCount)
                    : 'N/A'
                }
                helper="Rows embedded on the most recent pass"
              />
              <StatTile
                label="Duration"
                value={formatDurationMs(data.embeddingSync.lastSyncDurationMs)}
                helper={
                  data.embeddingSync.lastSyncError
                    ? `Last error: ${data.embeddingSync.lastSyncError}`
                    : 'No backfill error recorded'
                }
              />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Config versions</CardTitle>
          <CardDescription>
            Activate a prior version to roll back. An active version cannot be re-activated.
            Each row previews the tunables persisted with that version; values that differ from
            the currently active version are highlighted so a rollback is never blind.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {data.versions.map((version) => (
            <ConfigVersionRow
              key={version.id}
              version={version}
              activeTunables={data.tunables}
              canMutate={canMutate}
              activatePending={activate.isPending}
              onActivate={() => activate.mutate(version.id)}
            />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

interface TunableFieldDef {
  readonly key: keyof AdminTunables;
  readonly label: string;
  readonly format: (value: AdminTunables[keyof AdminTunables]) => string;
}

const TUNABLE_FIELDS: readonly TunableFieldDef[] = [
  {
    key: 'classifierThreshold',
    label: 'Classifier threshold',
    format: (v) => (typeof v === 'number' ? v.toFixed(2) : String(v ?? '—')),
  },
  {
    key: 'customerMatchThreshold',
    label: 'Customer match threshold',
    format: (v) => (typeof v === 'number' ? v.toFixed(2) : String(v ?? '—')),
  },
  {
    key: 'productMatchThreshold',
    label: 'Product match threshold',
    format: (v) => (typeof v === 'number' ? v.toFixed(2) : String(v ?? '—')),
  },
  {
    key: 'defaultLegalEntityId',
    label: 'Default legal entity',
    format: (v) =>
      typeof v === 'string' && v.length > 0 ? v.toUpperCase() : '— (no fallback)',
  },
  {
    key: 'defaultSiteId',
    label: 'Default site',
    format: (v) => (typeof v === 'string' && v.length > 0 ? v : '— (no fallback)'),
  },
  {
    key: 'acceptDuplicatedDocuments',
    label: 'Accept duplicated documents',
    format: (v) => (v ? 'On' : 'Off'),
  },
  {
    key: 'siteFeedbackFallbackEnabled',
    label: 'Site feedback fallback',
    format: (v) => (v ? 'On' : 'Off'),
  },
  {
    key: 'queueGroupingEnabled',
    label: 'Queue grouping enabled',
    format: (v) => (v ? 'On' : 'Off'),
  },
  {
    key: 'queueGroupingField',
    label: 'Queue grouping',
    format: (v) => {
      const option = QUEUE_GROUPING_OPTIONS.find((item) => item.value === v);
      return option?.label ?? String(v ?? 'PO');
    },
  },
  {
    key: 'queueGroupsDefaultExpanded',
    label: 'Queue groups default expanded',
    format: (v) => (v ? 'Expanded' : 'Collapsed'),
  },
];

function ConfigVersionRow({
  version,
  activeTunables,
  canMutate,
  activatePending,
  onActivate,
}: {
  version: ConfigVersion;
  activeTunables: AdminTunables;
  canMutate: boolean;
  activatePending: boolean;
  onActivate: () => void;
}): JSX.Element {
  const tunables = version.tunables;
  return (
    <div className="rounded-md border text-sm">
      <div className="flex items-start justify-between gap-4 p-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-sm font-medium" title={version.id}>
            <span className="truncate">
              {version.author} · {formatRelativeTime(version.createdAt)}
            </span>
            {version.active && <Badge variant="success">active</Badge>}
            {tunables === null && (
              <Badge variant="destructive">payload unreadable</Badge>
            )}
          </div>
          {version.description && (
            <div className="mt-0.5 text-xs text-muted-foreground">{version.description}</div>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={!canMutate || version.active || activatePending}
          onClick={onActivate}
        >
          <RotateCcw className="h-4 w-4" />
          Activate
        </Button>
      </div>
      {tunables && (
        <div className="grid grid-cols-1 gap-x-6 gap-y-1 border-t bg-muted/30 px-3 py-2 text-xs sm:grid-cols-2">
          {TUNABLE_FIELDS.map((field) => {
            const value = tunables[field.key];
            const activeValue = activeTunables[field.key];
            const differs = !version.active && value !== activeValue;
            return (
              <div
                key={String(field.key)}
                className="flex items-center justify-between gap-2 py-0.5"
              >
                <span className="text-muted-foreground">{field.label}</span>
                <span
                  className={cn(
                    'font-mono text-xs',
                    differs && 'rounded bg-amber-500/15 px-1.5 py-0.5 text-amber-700 dark:text-amber-400',
                  )}
                  title={
                    differs ? `Active: ${field.format(activeValue)}` : undefined
                  }
                >
                  {field.format(value)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ResetSyncButton({
  target,
  label,
  confirmMessage,
  canMutate,
}: {
  target: string;
  label: string;
  confirmMessage: string;
  canMutate: boolean;
}): JSX.Element {
  const reset = useAdminResetSync();
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={!canMutate || reset.isPending}
      onClick={() => {
        if (!window.confirm(confirmMessage)) return;
        reset.mutate(target, {
          onSuccess: () => toast.success(`${label} done.`),
          onError: (err) => toast.error(apiErrorMessage(err, 'Reset failed.')),
        });
      }}
    >
      <Trash2 className="h-4 w-4" />
      {label}
    </Button>
  );
}

function apiErrorMessage(err: unknown, fallback: string): string {
  const maybeResponse = err as { response?: { data?: { detail?: unknown; title?: unknown } } };
  const detail = maybeResponse.response?.data?.detail;
  if (typeof detail === 'string' && detail.length > 0) return detail;
  const title = maybeResponse.response?.data?.title;
  if (typeof title === 'string' && title.length > 0) return title;
  return err instanceof Error ? `${fallback} ${err.message}` : fallback;
}

function StatTile({
  label,
  value,
  helper,
}: {
  label: string;
  value: string;
  helper: string;
}): JSX.Element {
  return (
    <div className="rounded-md border px-3 py-3">
      <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
      <div className="mt-1 text-lg font-semibold">{value}</div>
      <div className="mt-1 text-xs text-muted-foreground">{helper}</div>
    </div>
  );
}

function SimpleSyncSampleList(props: {
  title: string;
  description: string;
  emptyMessage: string;
  items: readonly { id: string; title: string; subtitle: string; syncedAt: string }[];
}): JSX.Element {
  return (
    <div className="space-y-2">
      <div>
        <div className="text-sm font-medium">{props.title}</div>
        <div className="text-xs text-muted-foreground">{props.description}</div>
      </div>
      {props.items.length === 0 ? (
        <div className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
          {props.emptyMessage}
        </div>
      ) : (
        <div className="space-y-2">
          {props.items.map((sample) => (
            <div
              key={sample.id}
              className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
            >
              <div>
                <div className="font-medium">{sample.title}</div>
                <div className="text-xs text-muted-foreground">{sample.subtitle}</div>
              </div>
              <div className="text-right text-xs text-muted-foreground">
                <div>{formatRelativeTime(sample.syncedAt)}</div>
                <div>{new Date(sample.syncedAt).toLocaleString()}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatDurationMs(value: number | null): string {
  if (value === null) return 'N/A';
  if (value < 1_000) return `${value} ms`;
  const seconds = value / 1_000;
  if (seconds < 60) return `${seconds.toFixed(1)} s`;
  const minutes = Math.floor(seconds / 60);
  const remainderSeconds = Math.round(seconds % 60);
  return `${minutes}m ${remainderSeconds}s`;
}

function TunableRow({
  label,
  value,
  onChange,
  disabled = false,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between gap-4">
      <Label className="flex-1">{label}</Label>
      <Input
        type="number"
        step="0.01"
        min={0}
        max={1}
        className="w-32"
        value={value}
        onChange={(e) => onChange(parseNum(e.target.value, value))}
        disabled={disabled}
      />
    </div>
  );
}

function BooleanTunableRow({
  label,
  description,
  value,
  onChange,
  disabled = false,
}: {
  label: string;
  description?: string;
  value: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1 space-y-0.5">
        <Label className="text-sm font-medium">{label}</Label>
        {description ? (
          <p className="text-xs text-muted-foreground">{description}</p>
        ) : null}
      </div>
      <label
        className={cn(
          'inline-flex items-center gap-2 text-sm',
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
        )}
      >
        <input
          type="checkbox"
          aria-label={label}
          className="h-4 w-4 accent-primary"
          checked={value}
          onChange={(event) => onChange(event.target.checked)}
          disabled={disabled}
        />
        <span className="text-muted-foreground">{value ? 'On' : 'Off'}</span>
      </label>
    </div>
  );
}

function SelectTunableRow({
  label,
  value,
  placeholder,
  options,
  onChange,
  disabled = false,
}: {
  label: string;
  value: string | null;
  placeholder: string;
  options: readonly { value: string; label: string }[];
  onChange: (value: string | null) => void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <div className="grid gap-2">
      <Label className="text-sm font-medium">{label}</Label>
      <Select
        value={value ?? '__none__'}
        onValueChange={(next) => onChange(next === '__none__' ? null : next)}
        disabled={disabled}
      >
        <SelectTrigger aria-label={label}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">{placeholder}</SelectItem>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
